import "../components/campaignPicker.css";

import { Alert, Button } from "reactstrap";
import {
  requestFetchContractInfo,
  requestUpdateContractApplications,
} from "../services/backendRequests";

import { Drawer } from "../components/Drawer";
import { EmptyState } from "../components/EmptyState";
import { FilterBar } from "../clientDashboard/components/FilterBar";
import { InlineSpinner } from "../components/InlineSpinner";
import PropTypes from "prop-types";
import { useFetch } from "../hooks/useFetch";
import { useSelector } from "react-redux";
import { useState } from "react";

// Grants or revokes an application's access to campaigns. The link is not restricted to the
// application's own company, so every campaign is listed, grouped under its owning company:
// linking across companies is legitimate but must stay visible.
//
// Saving: the API replaces the whole application list of a campaign, so each campaign is re-read
// right before its write and its other applications are sent back untouched, with their roles.
// The new link gets no role, the public API only checks that the link exists.
export const ApplicationCampaignsDrawer = ({
  application,
  onClose,
  onSaved,
}) => {
  const user = useSelector((state) => state.user);
  const [campaigns, { loading }] = useFetch("/api/contract_infos");
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState([]);
  const [grantedOnly, setGrantedOnly] = useState(
    application.campaigns.length > 0
  );
  // `null` until the user ticks something, so the application's own list stays the reference
  const [picked, setPicked] = useState(null);
  const [alert, setAlert] = useState("");
  const [saving, setSaving] = useState(false);

  const initial = new Set(application.campaigns.map(({ id }) => id));
  const selected = picked ?? initial;

  const toggle = (campaignId) => {
    const next = new Set(selected);
    if (next.has(campaignId)) next.delete(campaignId);
    else next.add(campaignId);
    setPicked(next);
  };

  const changed = (campaigns ?? []).filter(
    (campaign) => selected.has(campaign.id) !== initial.has(campaign.id)
  );

  const save = async () => {
    setSaving(true);
    try {
      // Sequential on purpose: each write needs the campaign's current list, read just before
      for (const campaign of changed) {
        const fresh = await requestFetchContractInfo({ id: campaign.id }, user);
        const kept = fresh.applications
          .filter((app) => app.id !== application.id)
          .map((app) => ({ id: app.id, roles: app.roles ?? [] }));
        const next = selected.has(campaign.id)
          ? [...kept, { id: application.id, roles: [] }]
          : kept;
        await requestUpdateContractApplications(campaign.id, next, user);
      }
      onSaved(changed.length);
    } catch (error) {
      setAlert(String(error));
      setSaving(false);
    }
  };

  const companyOptions = [
    ...new Map(
      (campaigns ?? []).map(({ company }) => [company.id, company])
    ).values(),
  ]
    .map(({ id, name }) => ({ value: id, label: name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const needle = search.trim().toLowerCase();
  const visible = (campaigns ?? []).filter(
    (campaign) =>
      (!needle || campaign.name.toLowerCase().includes(needle)) &&
      (companyFilter.length === 0 ||
        companyFilter.includes(campaign.company.id)) &&
      // Filters on the saved links, not on the ticked boxes: otherwise a row would vanish the
      // moment it is unticked, and revoking an access would be unusable
      (!grantedOnly || initial.has(campaign.id))
  );

  // One block per owning company, so a grant outside the application's own company reads at a
  // glance instead of hiding in a flat list
  const groups = [
    ...visible
      .reduce((byCompany, campaign) => {
        const group = byCompany.get(campaign.company.id) ?? {
          company: campaign.company,
          campaigns: [],
        };
        group.campaigns.push(campaign);
        return byCompany.set(campaign.company.id, group);
      }, new Map())
      .values(),
  ].sort((a, b) => a.company.name.localeCompare(b.company.name));

  const title = (
    <span className="d-flex align-items-center justify-content-between w-100">
      {application.name}
      <span className="ms-3">{selected.size} granted</span>
    </span>
  );

  const footer = (
    <>
      <Button color="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button
        color="primary"
        disabled={changed.length === 0 || saving}
        onClick={save}
      >
        {saving ? <InlineSpinner text="Saving…" /> : `Save (${changed.length})`}
      </Button>
    </>
  );

  return (
    <Drawer isOpen onClose={onClose} title={title} footer={footer} wide>
      <Alert color="danger" isOpen={!!alert} toggle={() => setAlert("")}>
        {alert}
      </Alert>

      <FilterBar
        query={search}
        onQuery={setSearch}
        placeholder="Search a campaign…"
        selects={[
          {
            // No caption: the placeholder already names the filter, and captions would give the
            // three controls three different heights in a narrow drawer
            value: companyFilter,
            onChange: setCompanyFilter,
            allLabel: "All companies",
            options: companyOptions,
          },
        ]}
        extra={
          <label
            className={"cf-checkfilter" + (grantedOnly ? " is-active" : "")}
          >
            <input
              type="checkbox"
              checked={grantedOnly}
              onChange={(event) => setGrantedOnly(event.target.checked)}
            />
            Granted only
          </label>
        }
      />

      {loading && <InlineSpinner text="Loading campaigns…" />}

      {!loading && visible.length === 0 && (
        <EmptyState
          icon="fa-solid fa-file-signature"
          title="No campaign"
          text="No campaign matches your search."
        />
      )}

      {groups.map(({ company, campaigns: companyCampaigns }) => (
        <div className="cf-camp-group" key={company.id}>
          <div className="cf-camp-group__head">
            <span>{company.name}</span>
            <span className="cf-camp-group__count">
              {companyCampaigns.filter(({ id }) => selected.has(id)).length} /{" "}
              {companyCampaigns.length}
            </span>
          </div>
          {companyCampaigns.map((campaign) => (
            <label
              className={
                "cf-camp-row" + (selected.has(campaign.id) ? " is-granted" : "")
              }
              key={campaign.id}
            >
              <input
                className="form-check-input m-0 flex-shrink-0"
                type="checkbox"
                checked={selected.has(campaign.id)}
                onChange={() => toggle(campaign.id)}
              />
              <span className="cf-camp-row__name">{campaign.name}</span>
              {!campaign.active && (
                <span className="text-muted fst-italic small">inactive</span>
              )}
            </label>
          ))}
        </div>
      ))}
    </Drawer>
  );
};

ApplicationCampaignsDrawer.propTypes = {
  application: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    campaigns: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};
